// Cookie Consent Management Utility
// Handles cookie preferences and retargeting data storage

export type CookieConsentType = 'all' | 'essential' | 'declined' | null;

export interface CookiePreferences {
  consent: CookieConsentType;
  timestamp: number;
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface RetargetingData {
  visitorId: string;
  sessionCount: number;
  totalPageViews: number;
  pagesViewed: string[];
  interestCategories: string[];
  engagementLevel: 'low' | 'medium' | 'high';
  lastVisit: number;
  firstVisit: number;
  topClickedElements: string[];
  averageTimeOnSite: number;
  deviceType: string;
  browser: string;
  country: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
}

const CONSENT_KEY = 'xpand_cookie_consent';
const RETARGETING_KEY = 'xpand_retargeting_data';
const CONSENT_EXPIRY_DAYS = 365;

// Set a cookie with proper expiry
export function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

// Get a cookie value
export function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
}

// Delete a cookie
export function deleteCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

// Get current consent preferences
export function getConsentPreferences(): CookiePreferences | null {
  const stored = getCookie(CONSENT_KEY);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

// Save consent preferences
export function saveConsentPreferences(consent: CookieConsentType): CookiePreferences {
  const preferences: CookiePreferences = {
    consent,
    timestamp: Date.now(),
    essential: true, // Always enabled
    analytics: consent === 'all',
    marketing: consent === 'all',
  };
  
  setCookie(CONSENT_KEY, JSON.stringify(preferences), CONSENT_EXPIRY_DAYS);
  
  // Also store in localStorage for redundancy
  localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences));
  
  return preferences;
}

// Check if user has given consent for specific category
export function hasConsent(category: 'essential' | 'analytics' | 'marketing'): boolean {
  const prefs = getConsentPreferences();
  if (!prefs) return category === 'essential'; // Essential always allowed
  
  return prefs[category] ?? false;
}

// Check if consent banner should be shown
export function shouldShowConsentBanner(): boolean {
  // Check cookie first
  const cookieConsent = getCookie(CONSENT_KEY);
  if (cookieConsent) return false;
  
  // Check localStorage as fallback
  const localConsent = localStorage.getItem(CONSENT_KEY);
  if (localConsent) {
    // Sync to cookie
    try {
      const parsed = JSON.parse(localConsent);
      setCookie(CONSENT_KEY, localConsent, CONSENT_EXPIRY_DAYS);
      return false;
    } catch {
      // Invalid data, show banner
      return true;
    }
  }
  
  return true;
}

// Get retargeting data
export function getRetargetingData(): RetargetingData | null {
  const stored = localStorage.getItem(RETARGETING_KEY);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

// Update retargeting data
export function updateRetargetingData(updates: Partial<RetargetingData>): RetargetingData {
  const existing = getRetargetingData();
  const visitorId = localStorage.getItem('xpand_visitor_id') || crypto.randomUUID();
  
  const data: RetargetingData = {
    visitorId,
    sessionCount: existing?.sessionCount ?? 0,
    totalPageViews: existing?.totalPageViews ?? 0,
    pagesViewed: existing?.pagesViewed ?? [],
    interestCategories: existing?.interestCategories ?? [],
    engagementLevel: existing?.engagementLevel ?? 'low',
    lastVisit: Date.now(),
    firstVisit: existing?.firstVisit ?? Date.now(),
    topClickedElements: existing?.topClickedElements ?? [],
    averageTimeOnSite: existing?.averageTimeOnSite ?? 0,
    deviceType: existing?.deviceType ?? getDeviceType(),
    browser: existing?.browser ?? getBrowserName(),
    country: existing?.country ?? null,
    utmSource: existing?.utmSource ?? null,
    utmMedium: existing?.utmMedium ?? null,
    utmCampaign: existing?.utmCampaign ?? null,
    ...updates,
  };
  
  // Only store if user consented to marketing cookies
  if (hasConsent('marketing')) {
    localStorage.setItem(RETARGETING_KEY, JSON.stringify(data));
    // Also set a cookie for cross-domain retargeting
    setCookie(RETARGETING_KEY, JSON.stringify(data), 30);
  }
  
  return data;
}

// Add a page view to retargeting data
export function trackPageForRetargeting(pagePath: string): void {
  if (!hasConsent('marketing')) return;
  
  const data = getRetargetingData();
  const pagesViewed = data?.pagesViewed ?? [];
  
  // Add unique pages only
  if (!pagesViewed.includes(pagePath)) {
    pagesViewed.push(pagePath);
  }
  
  // Determine interest category from page path
  const category = categorizePageInterest(pagePath);
  const interestCategories = data?.interestCategories ?? [];
  if (category && !interestCategories.includes(category)) {
    interestCategories.push(category);
  }
  
  updateRetargetingData({
    pagesViewed,
    interestCategories,
    totalPageViews: (data?.totalPageViews ?? 0) + 1,
  });
}

// Categorize page interest for retargeting
function categorizePageInterest(path: string): string | null {
  const lowered = path.toLowerCase();
  
  if (lowered.includes('voice-ai') || lowered.includes('voice')) return 'voice-ai';
  if (lowered.includes('crm') || lowered.includes('smart-crm')) return 'crm';
  if (lowered.includes('booking') || lowered.includes('smart-booking')) return 'booking';
  if (lowered.includes('pricing')) return 'pricing-interest';
  if (lowered.includes('store') || lowered.includes('marketplace')) return 'marketplace';
  if (lowered.includes('schedule') || lowered.includes('demo')) return 'demo-interest';
  if (lowered.includes('solutions')) return 'solutions';
  if (lowered.includes('partners') || lowered.includes('affiliates')) return 'partnerships';
  if (lowered.includes('blog')) return 'content-reader';
  if (lowered.includes('docs') || lowered.includes('documentation')) return 'technical';
  
  return null;
}

// Update engagement level based on behavior
export function updateEngagementLevel(
  pageViews: number,
  timeOnSite: number,
  clicks: number
): 'low' | 'medium' | 'high' {
  let score = 0;
  
  // Page views scoring
  if (pageViews >= 5) score += 3;
  else if (pageViews >= 3) score += 2;
  else if (pageViews >= 1) score += 1;
  
  // Time on site scoring (in seconds)
  if (timeOnSite >= 300) score += 3; // 5+ minutes
  else if (timeOnSite >= 120) score += 2; // 2+ minutes
  else if (timeOnSite >= 30) score += 1; // 30+ seconds
  
  // Clicks scoring
  if (clicks >= 10) score += 3;
  else if (clicks >= 5) score += 2;
  else if (clicks >= 2) score += 1;
  
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  return 'low';
}

// Track clicked element for retargeting
export function trackClickForRetargeting(elementText: string): void {
  if (!hasConsent('marketing')) return;
  
  const data = getRetargetingData();
  const topClicked = data?.topClickedElements ?? [];
  
  // Track most clicked elements (max 20)
  const existing = topClicked.find(e => e === elementText);
  if (!existing && elementText.length > 0 && elementText.length < 50) {
    topClicked.push(elementText);
    if (topClicked.length > 20) topClicked.shift();
    
    updateRetargetingData({ topClickedElements: topClicked });
  }
}

// Increment session count
export function incrementSessionCount(): void {
  if (!hasConsent('marketing')) return;
  
  const data = getRetargetingData();
  updateRetargetingData({
    sessionCount: (data?.sessionCount ?? 0) + 1,
  });
}

// Helper functions
function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

function getBrowserName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Unknown';
}

// Clear all tracking data (for GDPR compliance)
export function clearAllTrackingData(): void {
  deleteCookie(CONSENT_KEY);
  deleteCookie(RETARGETING_KEY);
  localStorage.removeItem(CONSENT_KEY);
  localStorage.removeItem(RETARGETING_KEY);
  localStorage.removeItem('xpand_visitor_id');
  localStorage.removeItem('xpand_returning_visitor');
  sessionStorage.removeItem('xpand_session_id');
}
